import { Avatar, Divider, IconButton, type MenuProps } from '@affine/component';
import {
  type AuthAccountInfo,
  AuthService,
  ServerService,
  useCachedOwnAvatarUrl,
} from '@affine/core/modules/cloud';
import { GlobalDialogService } from '@affine/core/modules/dialogs';
import { WorkspaceQuotaService } from '@affine/core/modules/quota';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { Suspense, useCallback, useEffect, useRef } from 'react';

import { getStreamLainDropdownMenuStartAlignOffset } from '../../dropdown-panel-chrome/chrome';
import { StreamLainMenu } from '../../dropdown-panel-chrome/radix-menu';
import { Account } from './account';
import { AccountMenu } from './account-menu';
import { AIUsage } from './ai-usage';
import { CloudUsage } from './cloud-usage';
import * as styles from './index.css';
import { TeamList } from './team-list';
import { UnknownUserIcon } from './unknow-user';

export default function UserInfo() {
  const session = useService(AuthService).session;
  const account = useLiveData(session.account$);
  return account ? (
    <AuthorizedUserInfo account={account} />
  ) : (
    <UnauthorizedUserInfo />
  );
}

const SIDEBAR_USER_AVATAR_SIZE = 24;
const menuContentOptions = {
  align: 'start',
  alignOffset: -getStreamLainDropdownMenuStartAlignOffset(
    SIDEBAR_USER_AVATAR_SIZE
  ),
  className: styles.operationMenu,
  'data-testid': 'sidebar-user-menu',
} as MenuProps['contentOptions'];
const AuthorizedUserInfo = ({ account }: { account: AuthAccountInfo }) => {
  const avatarUrl = useCachedOwnAvatarUrl(account);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  return (
    <StreamLainMenu
      triggerRef={triggerRef}
      items={<OperationMenu account={account} />}
      contentOptions={menuContentOptions}
    >
      <IconButton
        ref={triggerRef}
        data-testid="sidebar-user-avatar"
        variant="plain"
        size={SIDEBAR_USER_AVATAR_SIZE}
        style={{ padding: 0 }}
        withoutHover
      >
        <Avatar
          size={SIDEBAR_USER_AVATAR_SIZE}
          name={account.label}
          style={{ transform: 'translateX(-0.5px)' }}
          url={avatarUrl}
        />
      </IconButton>
    </StreamLainMenu>
  );
};

const UnauthorizedUserInfo = () => {
  const globalDialogService = useService(GlobalDialogService);

  const openSignInModal = useCallback(() => {
    globalDialogService.open('sign-in', {});
  }, [globalDialogService]);

  return (
    <IconButton
      onClick={openSignInModal}
      data-testid="sidebar-user-avatar"
      variant="plain"
      size="20"
    >
      <UnknownUserIcon />
    </IconButton>
  );
};

const PlanBadge = () => {
  const t = useI18n();
  const quotaService = useService(WorkspaceQuotaService);
  const quota = useLiveData(quotaService.quota.quota$);

  useEffect(() => {
    quotaService.quota.revalidate();
  }, [quotaService]);

  const planName = quota?.name ?? t['com.streamlain.payment.pricing.free']();
  const isPaid = quota?.name !== undefined && quota.name !== 'Free';

  return (
    <div className={styles.planBadge} data-paid={isPaid}>
      {planName}
    </div>
  );
};

const OperationMenu = ({ account }: { account: AuthAccountInfo }) => {
  const serverService = useService(ServerService);
  const serverFeatures = useLiveData(serverService.server.features$);

  return (
    <>
      <Account
        account={account}
        badge={serverFeatures?.payment === false ? undefined : <PlanBadge />}
      />
      <Divider />
      <div className={styles.usageSection}>
        <CloudUsage />
        {serverFeatures?.copilot ? (
          <Suspense fallback={null}>
            <AIUsage />
          </Suspense>
        ) : null}
      </div>
      <Divider />
      <TeamList />
      <AccountMenu />
    </>
  );
};
